// English topic hubs (/topics/{slug}) — the English twin of /etiket/{slug}.
// Items are matched with the same curated tag vocabulary as the Turkish side,
// over English text only, so every hub lists real English pages.
import { ideas } from "@/content/en/ideas";
import { cop31NotesSorted, cop31NotePath } from "@/lib/cop31-notes";
import { deriveTags, slugifyTag, TAGS } from "@/lib/tags";
import { reportSlugMap } from "@/lib/report-slug";

export type EnTopicItem = {
  kind: "idea" | "cop31" | "report";
  title: string;
  summary: string;
  href: string;
  date?: string;
  sortKey: number;
};

type ReportLike = {
  id: string;
  title: string;
  organization?: string | null;
  year?: number | null;
  summary_en?: string | null;
};

/** Minimum items before a topic hub is published (matches the TR rule). */
export const EN_TOPIC_MIN = 2;

export function buildEnTopics(reports: ReportLike[]): Map<string, EnTopicItem[]> {
  const out = new Map<string, EnTopicItem[]>();
  const add = (tags: string[], item: EnTopicItem) => {
    for (const t of tags) {
      if (!out.has(t)) out.set(t, []);
      out.get(t)!.push(item);
    }
  };
  for (const i of ideas) {
    add(deriveTags(`${i.title} ${i.subtitle ?? ""} ${i.abstract ?? ""}`), {
      kind: "idea",
      title: i.title,
      summary: i.subtitle || i.abstract || "",
      href: `/ideas/${i.slug}`,
      date: i.published,
      sortKey: new Date(i.published).getTime() || 0,
    });
  }
  for (const n of cop31NotesSorted) {
    add(deriveTags(`${n.titleEn} ${n.summaryEn}`), {
      kind: "cop31",
      title: n.titleEn,
      summary: n.summaryEn,
      href: cop31NotePath(n.slug, "en"),
      date: n.iso,
      sortKey: new Date(n.iso).getTime() || 0,
    });
  }
  const slugs = reportSlugMap(reports);
  for (const r of reports) {
    add(deriveTags(`${r.title} ${r.summary_en ?? ""}`), {
      kind: "report",
      title: r.title,
      summary: r.summary_en ?? "",
      href: `/research/${slugs.get(r.id)}`,
      sortKey: (r.year ?? 0) * 1e10,
    });
  }
  for (const list of out.values()) list.sort((a, b) => b.sortKey - a.sortKey);
  // Keep only curated tags with enough depth.
  const known = new Set(TAGS.map((t) => t.slug));
  for (const [k, v] of out) if (!known.has(k) || v.length < EN_TOPIC_MIN) out.delete(k);
  return out;
}

/** English URL slug for a curated tag (e.g. "yesil-aklama" -> "greenwashing"). */
export function enTopicSlug(trSlug: string): string {
  const t = TAGS.find((x) => x.slug === trSlug);
  return t ? slugifyTag(t.en) : trSlug;
}

/** Curated tag slug for an English topic slug. */
export function trTopicSlug(enSlug: string): string | undefined {
  return TAGS.find((x) => slugifyTag(x.en) === enSlug)?.slug;
}
